import _ from "lodash";
import React, { useState, useContext, useEffect } from "react";
import classes from "./CollaboratorV2AddEditModal.scss";
import { useI18n } from "Hooks";
import { Tooltip, Modal, Button, DSProvider } from "@toddle-edu/ds-web";
import CollaboratorV2SearchDropdownList from "../CollaboratorV2SearchDropdownList";
import classNames from "classnames";
import { CollaboratorV2Context } from "AppComponents/CollaboratorV2/modules/Modules";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import { SHIFT_ENTER_SUBMIT_SHORTCUT } from "Constants/stringConstants";

const styles = {
  tooltipListStyle: {
    listStyleType: "none",
    padding: "0",
  },
  searchDropdownListContainerStyle: {},
};

const TEST_ID_PREFIX = "collaboratorV2-addEdit";

const CollaboratorV2AddEditModal = props => {
  const { onCloseModal, closeModalOnDone } = props;
  const { t } = useI18n(["common", "project"]);

  const contextValue = useContext(CollaboratorV2Context);
  const {
    labels,
    showUnsavedChangesWarning,
    selectedCollaborators = [],
    updateSelectedCollaborators: onDoneClick,
  } = contextValue;
  const {
    modalHeaderText,
    modalFooterCountText,
    modalHeaderTextLocale,
    modalHeaderSubText,
  } = labels;
  const [selectedOptions, setSelectedOptions] = useState(selectedCollaborators); // state for list/rows of selected members shown below
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false); // show UnsavedChanges warning if modal is closed with values in select dropdown pills
  const [isDoneButtonDisabled, setIsDoneButtonDisabled] = useState(false);

  useEffect(() => {
    setSelectedOptions(selectedCollaborators);
  }, [selectedCollaborators]);

  const updateDoneButtonDisabled = val => {
    setIsDoneButtonDisabled(val);
  };

  const updateSelectedOptions = value => {
    setSelectedOptions(value);
  };

  const onRemoveOption = removedId => {
    const updatedValue = _.filter(
      selectedOptions,
      item => item.id != removedId
    );
    updateSelectedOptions(updatedValue);
  };

  const getSelectedCollaboratorsTooltip = () => {
    return (
      <ul style={styles.tooltipListStyle}>
        {_.map(selectedOptions, collaborator => {
          const userName = getFormattedUserName({
            user: collaborator,
          });

          return <li>{userName}</li>;
        })}
      </ul>
    );
  };

  const handleDoneClick = () => {
    onDoneClick(selectedOptions);
    if (closeModalOnDone) onCloseModal();
  };

  const toggleUnsavedChangesModal = () => {
    setShowUnsavedChangesModal(prevState => {
      return !prevState;
    });
  };

  const onCloseAddEditModal = () => {
    // check if there is any change in state of collaborators, to trigger unsaved changes modal
    const isCollaboratorRemoved =
      _.size(_.differenceBy(selectedCollaborators, selectedOptions, "id")) != 0;
    const isCollaboratorAdded =
      _.size(_.differenceBy(selectedOptions, selectedCollaborators, "id")) != 0;

    if (
      showUnsavedChangesWarning &&
      (isCollaboratorRemoved || isCollaboratorAdded)
    ) {
      toggleUnsavedChangesModal();
    } else {
      onCloseModal();
    }
  };

  const onSaveAndExitClick = () => {
    toggleUnsavedChangesModal();
    handleDoneClick(selectedOptions);
  };

  const onDiscardAndExitClick = () => {
    toggleUnsavedChangesModal();
    onCloseModal();
  };
  return (
    <Modal
      onClose={onCloseAddEditModal}
      height="600px"
      width="480px"
      dsVersion="2.0"
    >
      <Modal.Header
        title={t(modalHeaderText, {
          label: t(modalHeaderTextLocale),
        })}
        subtitle={modalHeaderSubText ? t(modalHeaderSubText) : ""}
        hasSeparator={true}
        testId={TEST_ID_PREFIX}
      />
      <Modal.Body isScrollable>
        <DSProvider dsVersion="2.0">
          <div className={classes.searchDropdownListContainer}>
            {
              <CollaboratorV2SearchDropdownList
                selectedOptions={selectedOptions}
                onRemoveOption={onRemoveOption}
                onAddOptions={updateSelectedOptions}
                showListHeader={true}
                searchDropdownListContainerStyle={
                  styles.searchDropdownListContainerStyle
                }
                updateDoneButtonDisabled={updateDoneButtonDisabled}
                testId={TEST_ID_PREFIX}
              />
            }
          </div>
        </DSProvider>
      </Modal.Body>
      <Modal.Footer hasSeparator>
        <div className={classes.footerContainer}>
          {_.size(selectedOptions) != 0 ? (
            <Tooltip
              tooltip={getSelectedCollaboratorsTooltip()}
              placement="bottom"
              dsVersion="2.0"
              tabIndex={0}
            >
              <div
                className={classNames(classes.countContainer, "text-label")}
                data-test-id={`${TEST_ID_PREFIX}-selectedCount`}
                data-value={_.size(selectedOptions)}
              >
                {t(modalFooterCountText, {
                  count: _.size(selectedOptions),
                })}
              </div>
            </Tooltip>
          ) : null}
          <div className={classes.buttonContainer}>
            <Button
              onClick={handleDoneClick}
              disabled={isDoneButtonDisabled}
              dsVersion="2.0"
              type="fill"
              shortcut={SHIFT_ENTER_SUBMIT_SHORTCUT}
              variant="primary"
              size="medium"
              testId={`${TEST_ID_PREFIX}-done`}
            >
              {t("common:done")}
            </Button>
          </div>
        </div>
      </Modal.Footer>

      {showUnsavedChangesModal ? (
        <Modal
          height="fit-content"
          width="480px"
          onClose={toggleUnsavedChangesModal}
          dsVersion="2.0"
        >
          <Modal.Header
            title={t(labels.unsavedChangesModalHeader)}
            testId={`${TEST_ID_PREFIX}-unsavedChanges`}
          ></Modal.Header>
          <Modal.Body>
            {t(labels.unsavedChangesModalBody, {
              label: t(labels.unsavedChangesModalBodyLocale),
            })}
          </Modal.Body>
          <Modal.Footer>
            <div className={classes.unsavedChangesModalButtons}>
              <Button
                onClick={onSaveAndExitClick}
                variant={"neutral"}
                size="medium"
                dsVersion="2.0"
                type="outlined"
                testId={`${TEST_ID_PREFIX}-saveAndExit`}
              >
                {t("common:save_and_exit")}
              </Button>
              <Button
                onClick={onDiscardAndExitClick}
                dsVersion="2.0"
                type="fill"
                shortcut={SHIFT_ENTER_SUBMIT_SHORTCUT}
                variant="primary"
                size="medium"
                testId={`${TEST_ID_PREFIX}-discardAndExit`}
              >
                {t("common:discard_and_exit")}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      ) : null}
    </Modal>
  );
};

export default CollaboratorV2AddEditModal;
